import React, { useState, useEffect } from 'react';
import api from '../components/api';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import {
  EtablissementHeader,
  EtablissementMainInfo,
  EtablissementDetails,
  EtablissementEmptyState,
  EtablissementForm
} from '../components/etablissement';
import '../components/etablissement/EtablissementStyles.css';

const EtablissementPage = () => {
  const [etablissement, setEtablissement] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    libelle: '',
    contact: '',
    email: '',
    date_creation: '',
    devise: '',
    bp: '',
    pays: '',
    adresse: '',
    site_web: '',
    ministere: '',
    pourcentage_note_controle: '',
    pourcentage_note_examen: '',
    have_abonnement: false,
    abonnement_date_debut: '',
    abonnement_date_fin: '',
    abonnement_montant: '',
    abonnement_status: 'en_cours',
    abonnement_description: '',
    logo: null,
  });

  useEffect(() => {
    fetchEtablissement();
  }, []);

  const fetchEtablissement = async () => {
    try {
      const response = await api.get('/etablissement/etablissement-infos/');
      if (response.data) {
        setEtablissement(response.data);
        setFormData(response.data);
      }
    } catch (error) {
      if (error.response && error.response.status === 404) {
        toast.info(error.response.data.message || "Aucun établissement trouvé");
      } else {
        toast.error('Erreur lors de la récupération des informations');
      }
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      
      // S'assurer que tous les champs obligatoires sont présents
      if (!formData.libelle || !formData.contact || !formData.email || 
          !formData.devise || !formData.bp || !formData.pays || 
          !formData.adresse || !formData.ministere) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        setLoading(false);
        return;
      }
      
      // Valider l'URL du site web si présente
      if (formData.site_web && formData.site_web.trim() !== '') {
        try {
          // Vérifier si l'URL est valide
          new URL(formData.site_web);
          // S'assurer que l'URL a un protocole (http:// ou https://)
          if (!formData.site_web.startsWith('http://') && !formData.site_web.startsWith('https://')) {
            formData.site_web = 'https://' + formData.site_web;
          }
        } catch (e) {
          toast.error('L\'URL du site web n\'est pas valide. Utilisez un format comme https://example.com');
          setLoading(false);
          return;
        }
      }
      
      // Validation des pourcentages
      const pourcentageControle = parseInt(formData.pourcentage_note_controle) || 0;
      const pourcentageExamen = parseInt(formData.pourcentage_note_examen) || 0;
      
      // Vérifier que les pourcentages sont des nombres valides
      if (isNaN(pourcentageControle) || isNaN(pourcentageExamen)) {
        toast.error('Les pourcentages doivent être des nombres valides');
        setLoading(false);
        return;
      }
      
      // Vérifier que les pourcentages sont entre 0 et 100
      if (pourcentageControle < 0 || pourcentageControle > 100 || 
          pourcentageExamen < 0 || pourcentageExamen > 100) {
        toast.error('Les pourcentages doivent être compris entre 0 et 100');
        setLoading(false);
        return;
      }
      
      // Vérifier que la somme ne dépasse pas 100%
      if (pourcentageControle + pourcentageExamen > 100) {
        toast.error('La somme des pourcentages ne peut pas dépasser 100%');
        setLoading(false);
        return;
      }
      
      // Copie des données de formulaire pour traitement
      const processedFormData = { ...formData };
      
      // Si nous mettons à jour un établissement existant, assurons-nous que l'ID est inclus
      if (etablissement && etablissement.id) {
        processedFormData.id = etablissement.id;
      }
      
      // Traiter les champs vides/null/undefined avant d'ajouter au FormData
      Object.keys(processedFormData).forEach(key => {
        // Pour les champs qui peuvent être null (comme date_creation)
        if (key === 'date_creation') {
          if (!processedFormData[key] || processedFormData[key] === '') {
            // Pour la date, nous devons supprimer le champ au lieu d'envoyer null
            delete processedFormData[key];
          } else {
            // Formater la date correctement
            const date = new Date(processedFormData[key]);
            if (!isNaN(date.getTime())) {
              processedFormData[key] = date.toISOString().split('T')[0];
            }
          }
        }
        
        // Pour site_web, envoyer null explicitement si vide
        if (key === 'site_web' && (!processedFormData[key] || processedFormData[key] === '')) {
          processedFormData[key] = null;
        }

        // Traitement spécial pour les champs d'abonnement
        if (key === 'abonnement_date_debut' || key === 'abonnement_date_fin') {
          if (!processedFormData[key] || processedFormData[key] === '') {
            // Supprimer le champ s'il est vide
            delete processedFormData[key];
          } else {
            // Formater la date correctement
            const date = new Date(processedFormData[key]);
            if (!isNaN(date.getTime())) {
              processedFormData[key] = date.toISOString().split('T')[0];
            } else {
              // Si la date n'est pas valide, supprimer le champ
              delete processedFormData[key];
            }
          }
        }

        // Pour abonnement_montant, gérer les valeurs vides
        if (key === 'abonnement_montant') {
          if (!processedFormData[key] || processedFormData[key] === '' || processedFormData[key] === '0') {
            // Supprimer le champ s'il est vide ou égal à 0
            delete processedFormData[key];
          } else {
            // S'assurer que c'est un nombre valide
            const montant = parseInt(processedFormData[key]);
            if (isNaN(montant) || montant <= 0) {
              delete processedFormData[key];
            } else {
              processedFormData[key] = montant;
            }
          }
        }

        // Pour abonnement_description, envoyer null si vide
        if (key === 'abonnement_description' && (!processedFormData[key] || processedFormData[key] === '')) {
          processedFormData[key] = null;
        }

        // Traitement des pourcentages
        if (key === 'pourcentage_note_controle' || key === 'pourcentage_note_examen') {
          if (!processedFormData[key] || processedFormData[key] === '') {
            processedFormData[key] = 0;
          } else {
            // S'assurer que c'est un nombre entier valide
            const pourcentage = parseInt(processedFormData[key]);
            if (isNaN(pourcentage)) {
              processedFormData[key] = 0;
            } else {
              processedFormData[key] = pourcentage;
            }
          }
        }
      });
      
      // Ajouter les champs traités au FormData
      Object.keys(processedFormData).forEach(key => {
        // Ne pas envoyer logo_url ni d'autres champs calculés
        if (key !== 'logo_url' && key !== 'logo' && processedFormData[key] !== undefined) {
          formDataToSend.append(key, processedFormData[key]);
        }
      });

      // Traitement spécial pour le logo
      // N'ajouter le logo que s'il s'agit d'un objet File (nouveau logo)
      if (formData.logo instanceof File) {
        formDataToSend.append('logo', formData.logo);
      }

      let response;

      if (etablissement) {
        // Mise à jour - utiliser PUT comme spécifié dans la vue Django
        response = await api.put('/etablissement/etablissement-infos/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success('Informations mises à jour avec succès');
      } else {
        // Création
        response = await api.post('/etablissement/etablissement-infos/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success('Informations créées avec succès');
      }
      
      fetchEtablissement();
      handleCloseDialog();
    } catch (error) {
      // Afficher les détails bruts de l'erreur dans la console
      if (error.response) {
        let errorMessage = '';
        
        // Traitement spécifique pour les erreurs de validation
        if (typeof error.response.data === 'object') {
          // Afficher toutes les erreurs de validation
          Object.entries(error.response.data).forEach(([field, errors]) => {
            if (Array.isArray(errors)) {
              errorMessage += `${field}: ${errors.join(', ')}\n`;
            } else if (typeof errors === 'string') {
              errorMessage += `${field}: ${errors}\n`;
            } else if (typeof errors === 'object') {
              errorMessage += `${field}: ${JSON.stringify(errors)}\n`;
            }
          });
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
        
        if (errorMessage) {
          toast.error(`Erreurs de validation:\n${errorMessage}`);
          return;
        }
      }
      
      // Message d'erreur par défaut
      toast.error(
        error.response?.data?.message || 
        error.response?.data?.detail ||
        'Une erreur est survenue lors de la sauvegarde. Vérifiez les champs uniques comme l\'email et le contact.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <EtablissementHeader 
            etablissement={etablissement} 
            onEditClick={handleOpenDialog} 
          />

          {/* Main Content */}
          {etablissement ? (
            <div className="space-y-8">
              {/* Main Info Section */}
              <EtablissementMainInfo etablissement={etablissement} />
              
              {/* Details Section */}
              <EtablissementDetails etablissement={etablissement} />
            </div>
          ) : (
            <EtablissementEmptyState onAddClick={handleOpenDialog} />
          )}

          {/* Form Modal */}
          <EtablissementForm
            etablissement={etablissement}
            openDialog={openDialog}
            onClose={handleCloseDialog}
            onSubmit={handleSubmit}
            loading={loading}
            formData={formData}
            setFormData={setFormData}
          />
        </div>
      </div>
    </Layout>
  );
};

export default EtablissementPage;
